import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import BayerTabs from '/@/components/bayer/tabs';
import useUnitProvider from '/@/core/context/unit';
import { useGrowersDashboardReports } from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import type { CropType } from '/@/types/crop-monitoring';
import { sliceString } from '/@/utils';
import type { NodeScopeFilters } from '/@/utils/node-scope';

const FieldsRankingReportDashboardCard = ({
    growerNodeId,
    year,
    crop,
    nodeScope,
}: {
    growerNodeId: number;
    year: number;
    crop: CropType;
    nodeScope: NodeScopeFilters;
}) => {
    const [currentFieldZone, setCurrentFieldZone] = useState<string | undefined>(undefined);
    const units = useUnitProvider();

    const { data: dashboardReports, isValidating } = useGrowersDashboardReports({
        year,
        node: growerNodeId,
        crop,
        ...nodeScope,
    });

    const parsedYieldByFields = dashboardReports?.harvest?.avgYieldByFields?.reduce(
        (acc, field) => {
            const zone = _(field.zoneShortName);
            if (!acc[zone]) {
                acc[zone] = [];
            }
            acc[zone].push(field);
            return acc;
        },
        {} as Record<string, NonNullable<typeof dashboardReports>['harvest']['avgYieldByFields']>
    );

    useEffect(() => {
        // Set initial zones if not set
        if (!currentFieldZone && dashboardReports?.avaiableZones?.length > 0) {
            setCurrentFieldZone(dashboardReports.avaiableZones[0]?.value as string);
        }
    }, [currentFieldZone, dashboardReports?.avaiableZones]);

    return (
        <DashboardCard
            title={_('Ranking lotes')}
            isLoading={isValidating}
            subtitle={_('Mejor rendimiento promedio de maíz por lote.')}
        >
            <Stack direction='column' spacing={2}>
                <BayerTabs
                    value={currentFieldZone}
                    setValue={setCurrentFieldZone}
                    options={
                        dashboardReports?.zones?.map((zone) => ({
                            label: zone.value as string,
                            value: zone.value as string,
                            disabled: zone.isDisabled,
                            tooltipText: zone.isDisabled ? _('No hay información para esta zona') : '',
                        })) || []
                    }
                    variant='chips'
                />
                <Stack direction='column' spacing={0.5}>
                    {parsedYieldByFields?.[currentFieldZone]?.map((fieldYield) => {
                        const slicedName = sliceString(fieldYield.field.name, 20);
                        return (
                            <Stack key={fieldYield.field.id} direction='row' spacing={2} justifyContent='space-between'>
                                <Typography variant='subtitle1' color='primary'>
                                    {slicedName}
                                </Typography>
                                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                                    {units.toYield(fieldYield.avgYield, 'corn', 0).toString()}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>
        </DashboardCard>
    );
};

export default FieldsRankingReportDashboardCard;
